import Vue from "vue";
import Router from "vue-router";
import store from "@/store/";
import { hasPermission } from "@/mixin";

// Containers
import Checkout from "@/containers/Checkout";
import Full from "@/containers/Full";
import Login from "@/containers/Login";
import PasswordReset from "@/containers/PasswordReset";

// ** Views **
// attribution
import Attributions from "@/views/attribution/Attributions";
import AttributionsClient from "@/views/attribution/AttributionsClient";
import AttributionsOrder from "@/views/attribution/AttributionsOrder";
import AttributionsSession from "@/views/attribution/AttributionsSession";

// billing
import Billing from "@/views/billing/Billing";
import Invoices from "@/views/billing/Invoices";
import Payments from "@/views/billing/Payments";
import Payouts from "@/views/billing/Payouts";
import Unpaid from "@/views/billing/Unpaid";

// client
import ClientMapTab from "@/views/client/ClientMapTab";
import Clients from "@/views/client/Clients";
import ClientsContainer from "@/views/client/ClientsContainer";
import ClientsDetails from "@/views/client/ClientsDetails";
import ClientsLostRelaunchTab from "@/views/client/ClientsLostRelaunchTab";
import ClientsNav from "@/views/client/ClientsNav";
import ClientsProspectsLostRelaunchContainer from "@/views/client/ClientsProspectsLostRelaunchContainer";
import ClientTab from "@/views/client/ClientTab";
import ProspectMapTab from "@/views/client/ProspectMapTab";
import ProspectsContainer from "@/views/client/ProspectsContainer";
import ProspectsLostRelaunchTab from "@/views/client/ProspectsLostRelaunchTab";
import ProspectTab from "@/views/client/ProspectTab";

// coach
import Disbursements from "@/views/coach/Disbursements";
import DisbursementsBlocked from "@/views/coach/DisbursementsBlocked";
import DisbursementsPayed from "@/views/coach/DisbursementsPayed";
import DisbursementsPending from "@/views/coach/DisbursementsPending";
import DisbursementsProcessed from "@/views/coach/DisbursementsProcessed";
import DisbursementsTroubles from "@/views/coach/DisbursementsTroubles";
import Documents from "@/views/coach/Documents";
import Substitutions from "@/views/coach/Substitutions";
import Users from "@/views/coach/Users";
import UsersDetails from "@/views/coach/UsersDetails";
import UsersNav from "@/views/coach/UsersNav";
import UsersProfilePictures from "@/views/coach/UsersProfilePictures";

// coach-manager
import CoachManager from "@/views/coach-manager/CoachManager";
import CoachManagerAttributionsTreated from "@/views/coach-manager/CoachManagerAttributionsTreated";
import CoachManagerClientsNote from "@/views/coach-manager/CoachManagerClientsNote";
import CoachManagerConversion from "@/views/coach-manager/CoachManagerConversion";
import CoachManagerFidelisation from "@/views/coach-manager/CoachManagerFidelisation";
import CoachManagerInternalNote from "@/views/coach-manager/CoachManagerInternalNote";
import CoachManagerLongevity from "@/views/coach-manager/CoachManagerLongevity.vue";
import CoachManagerLost from "@/views/coach-manager/CoachManagerLost";
import CoachManagerScrapping from "@/views/coach-manager/CoachManagerScrapping";
import CoachManagerStatus from "@/views/coach-manager/CoachManagerStatus";

// company
import CompaniesList from "@/views/company/CompaniesList";
import CompaniesNav from "@/views/company/CompaniesNav";
import CompanyDetails from "@/views/company/CompanyDetails";
import PublicProcurementsList from "@/views/company/PublicProcurementsList";
import SessionsBatches from "@/views/company/SessionsBatches";
import SignatureSheetsNav from "@/views/company/SignatureSheetsNav";
import SignatureSheetsList from "@/views/company/SignatureSheetsList";
import SignatureSheetTemplates from "@/views/company/SignatureSheetTemplates";

// content
import Contents from "@/views/content/Contents";
import DynamicContentClientApp from "@/views/content/DynamicContentClientApp";
import DynamicContentClientAppNews from "@/views/content/DynamicContentClientAppNews";
import DynamicContentClientAppNotifications from "@/views/content/DynamicContentClientAppNotifications";
import DynamicContentCoachApp from "@/views/content/DynamicContentCoachApp";
import DynamicContentCoachAppAppUpdates from "@/views/content/DynamicContentCoachAppAppUpdates";
import DynamicContentCoachAppSpecialEvents from "@/views/content/DynamicContentCoachAppSpecialEvents";
import DynamicContentCoachAppTutorials from "@/views/content/DynamicContentCoachAppTutorials";
import DynamicContentNav from "@/views/content/DynamicContentNav";
import Faq from "@/views/content/Faq";
import FaqDetails from "@/views/content/FaqDetails";

// conversion
import Conversion from "@/views/conversion/Conversion";

// dashboard
import Dashboard from "@/views/dashboard/Dashboard";

// fidelisation
import Fidelisation from "@/views/fidelisation/Fidelisation";

// schedule tracking
import SessionScheduleTracking from "@/views/schedule-tracking/SessionScheduleTracking";

// b2b tracking
import B2BTracking from "@/views/b2b-tracking/B2BTracking";

// export
import Export from "@/views/export/Export";
import ExportAccountingMarginRate from "@/views/export/ExportAccountingMarginRate.vue";
import ExportClients from "@/views/export/ExportClients";
import ExportCompta from "@/views/export/ExportCompta";
import ExportMarketingForm from "@/views/export/ExportMarketingForm";
import ExportNova from "@/views/export/ExportNova";
import ExportOrders from "@/views/export/ExportOrders";
import ExportPrestataires from "@/views/export/ExportPrestataires";
import ExportProspects from "@/views/export/ExportProspects";
import ExportSoldeComptable from "@/views/export/ExportSoldeComptable";

// import
import Import from "@/views/import/Import";
import ImportPrestataires from "@/views/import/ImportPrestataires";

// order
import OrdersDetails from "@/views/order/OrdersDetails";
import OrdersList from "@/views/order/OrdersList";
import OrdersMaps from "@/views/order/OrdersMaps.vue";
import OrdersNav from "@/views/order/OrdersNav.vue";

// search
import Search from "@/views/search/Search";

// settings
import Merchants from "@/views/settings/Merchants";
import MerchantsDetails from "@/views/settings/MerchantsDetails";
import Settings from "@/views/settings/Settings";
import SettingsConfigs from "@/views/settings/SettingsConfigs";
import SettingsConcepts from "@/views/settings/SettingsConcepts";
import SettingsConceptsDetails from "@/views/settings/SettingsConceptsDetails";
import SettingsIntervenants from "@/views/settings/SettingsIntervenants";
import SettingsPaymentMethods from "@/views/settings/SettingsPaymentMethods";
import SettingsPaymentMethodsDetails from "@/views/settings/SettingsPaymentMethodsDetails";
import SettingsSap from "@/views/settings/SettingsSap";
import SettingsTeamMembers from "@/views/settings/SettingsTeamMembers";
import SettingsTeamMembersDetails from "@/views/settings/SettingsTeamMembersDetails";

// stats
import Stats from "@/views/stats/Stats";
import StatsAcquisitionFidelisation from "@/views/stats/StatsAcquisitionFidelisation";
import StatsAttribution from "@/views/stats/StatsAttribution";
import StatsCARatios from "@/views/stats/StatsCARatios";
import StatsCARatiosCouts from "@/views/stats/StatsCARatiosCouts";
import StatsCommercials from "@/views/stats/StatsCommercials";
import StatsExpense from "@/views/stats/StatsExpense";
import StatsFormExpense from "@/views/stats/StatsFormExpense";
import StatsLost from "@/views/stats/StatsLost";
import StatsLostMap from "@/views/stats/StatsLostMap";
import StatsLostProspectsClients from "@/views/stats/StatsLostProspectsClients";
import StatsLostSources from "@/views/stats/StatsLostSources";
import StatsMarginRate from "@/views/stats/StatsMarginRate";
import StatsMarketing from "@/views/stats/StatsMarketing";
import StatsMarketingKpi from "@/views/stats/StatsMarketingKpi";
import StatsMarketingKpiExport from "@/views/stats/StatsMarketingKpiExport";
import StatsPayments from "@/views/stats/StatsPayments";
import StatsROAS from "@/views/stats/StatsROAS";
import StatsStatsCommercialsV2 from "@/views/stats/StatsStatsCommercialsV2";
import StatsB2C2B from "@/views/stats/StatsB2C2B";

Vue.use(Router);

const router = new Router({
  mode: "hash",
  linkActiveClass: "open active",
  scrollBehavior: () => ({ y: 0 }),
  routes: [
    {
      name: "login",
      path: "/login",
      component: Login,
    },
    {
      name: "password-reset",
      path: "/password-reset/:key",
      component: PasswordReset,
    },
    {
      name: "stripe",
      path: "/order/:id/:success?",
      component: Checkout,
    },
    {
      path: "/",
      name: "Home",
      component: Full,
      children: [
        {
          path: "search",
          name: "search",
          meta: {
            label: "Recherche",
          },
          component: Search,
        },
        {
          path: "dashboard",
          name: "dashboard",
          meta: {
            label: "Dashboard",
            requiredPermission: "DASHBOARD_READ",
          },
          component: Dashboard,
        },
        {
          path: "orders",
          name: "orders",
          meta: {
            label: "Commandes",
            requiredPermission: "ORDERS_READ",
          },
          component: OrdersNav,
          redirect: () => {
            return { name: "orders-list" };
          },
          children: [
            {
              path: "list",
              name: "orders-list",
              meta: {
                label: "Liste des commandes",
                requiredPermission: "ORDERS_READ",
              },
              component: OrdersList,
              children: [],
            },
            {
              path: "maps",
              name: "orders-maps",
              meta: {
                label: "Cartes des commandes",
                requiredPermission: "ORDERS_READ",
              },
              component: OrdersMaps,
            },
            {
              path: ":id",
              component: OrdersDetails,
              name: "order-details",
              meta: {
                label: "Détails de la commande",
                requiredPermission: "ORDERS_READ",
              },
            },
          ],
        },
        {
          path: "billing",
          name: "billing",
          meta: {
            label: "Facturation",
            requiredPermission: "BILLING_READ",
          },
          component: Billing,
          redirect: () => {
            if (hasPermission(store.state.user, "INVOICES_READ")) {
              return { name: "invoices" };
            }

            if (hasPermission(store.state.user, "PAYMENTS_READ")) {
              return { name: "payments" };
            }

            if (hasPermission(store.state.user, "PAYOUTS_READ")) {
              return { name: "payouts" };
            }

            if (hasPermission(store.state.user, "UNPAID_READ")) {
              return { name: "unpaid" };
            }

            return;
          },
          children: [
            {
              path: "invoices",
              name: "invoices",
              meta: {
                label: "Factures",
                requiredPermission: "INVOICES_READ",
              },
              component: Invoices,
            },
            {
              name: "payments",
              path: "payments",
              meta: {
                label: "Paiements",
                requiredPermission: "PAYMENTS_READ",
              },
              component: Payments,
            },
            {
              name: "payouts",
              path: "payouts",
              meta: {
                label: "Virement depuis Stripe",
                requiredPermission: "PAYOUTS_READ",
              },
              component: Payouts,
            },
            {
              name: "unpaid",
              path: "unpaid",
              meta: {
                label: "Impayés",
                requiredPermission: "UNPAID_READ",
              },
              component: Unpaid,
            },
          ],
        },
        {
          path: "attributions",
          name: "attributions",
          meta: {
            label: "Attributions",
            requiredPermission: "ATTRIBUTIONS_READ",
          },
          component: Attributions,
          children: [
            {
              path: "client/:id",
              component: AttributionsClient,
              name: "attributions-client",
              meta: {
                label: "Client",
                requiredPermission: "ATTRIBUTIONS_READ",
              },
            },
            {
              path: "order/:id",
              component: AttributionsOrder,
              name: "attributions-order",
              meta: {
                label: "Commande",
                requiredPermission: "ATTRIBUTIONS_READ",
              },
            },
            {
              path: "session/:id",
              component: AttributionsSession,
              name: "attributions-session",
              meta: {
                label: "Séance",
                requiredPermission: "ATTRIBUTIONS_READ",
              },
            },
          ],
        },
        {
          path: "clients",
          name: "clientsNav",
          meta: {
            label: "Prospects - Clients",
            requiredPermission: "CLIENTS_READ",
          },
          component: ClientsNav,
          redirect: () => {
            return { name: "data" };
          },
          children: [
            {
              path: "data",
              name: "data",
              meta: {
                label: "Données",
                requiredPermission: "CLIENTS_READ",
              },
              component: Clients,
              redirect: () => {
                return { name: "prospects" };
              },
              children: [
                {
                  path: "prospects",
                  name: "prospects",
                  meta: {
                    label: "Prospects",
                    requiredPermission: "CLIENTS_READ",
                  },
                  component: ProspectsContainer,
                  redirect: () => {
                    return { name: "prospects-list" };
                  },
                  children: [
                    {
                      path: "list",
                      name: "prospects-list",
                      meta: {
                        label: "Liste",
                        requiredPermission: "CLIENTS_READ",
                      },
                      component: ProspectTab,
                    },
                    {
                      path: "map",
                      name: "prospects-map",
                      meta: {
                        label: "Carte",
                        requiredPermission: "CLIENTS_READ",
                      },
                      component: ProspectMapTab,
                    },
                  ],
                },
                {
                  path: "clients",
                  name: "clients",
                  meta: {
                    label: "Clients",
                    requiredPermission: "CLIENTS_READ",
                  },
                  component: ClientsContainer,
                  redirect: () => {
                    return { name: "clients-list" };
                  },
                  children: [
                    {
                      path: "list",
                      name: "clients-list",
                      meta: {
                        label: "Liste",
                        requiredPermission: "CLIENTS_READ",
                      },
                      component: ClientTab,
                    },
                    {
                      path: "map",
                      name: "clients-map",
                      meta: {
                        label: "Carte",
                        requiredPermission: "CLIENTS_READ",
                      },
                      component: ClientMapTab,
                    },
                  ],
                },
              ],
            },
            {
              path: "lost-relaunch",
              name: "lost-relaunch",
              meta: {
                label: "Relance perdus",
                requiredPermission: "CLIENTS_READ",
              },
              component: ClientsProspectsLostRelaunchContainer,
              redirect: () => {
                return { name: "prospects-lost-relaunch-list" };
              },
              children: [
                {
                  path: "prospects",
                  name: "prospects-lost-relaunch-list",
                  meta: {
                    label: "Prospects",
                    requiredPermission: "CLIENTS_READ",
                  },
                  component: ProspectsLostRelaunchTab,
                },
                {
                  path: "clients",
                  name: "clients-lost-relaunch-list",
                  meta: {
                    label: "Clients",
                    requiredPermission: "CLIENTS_READ",
                  },
                  component: ClientsLostRelaunchTab,
                },
              ],
            },
          ],
        },
        {
          path: "clients/:id",
          name: "client-details",
          component: ClientsDetails,
          meta: {
            label: "Fiche client",
            requiredPermission: "CLIENTS_READ",
          },
        },
        {
          path: "users",
          name: "usersNav",
          meta: {
            label: "Prestataires",
            requiredPermission: "USERS_MENU_READ",
          },
          component: UsersNav,
          redirect: () => {
            if (hasPermission(store.state.user, "USERS_READ")) {
              return { name: "users" };
            }

            if (hasPermission(store.state.user, "DOCUMENTS_READ")) {
              return { name: "documents" };
            }

            if (hasPermission(store.state.user, "DISBURSEMENTS_READ")) {
              return { name: "disbursements" };
            }

            if (hasPermission(store.state.user, "SUBSTITUTIONS_READ")) {
              return { name: "substitutions" };
            }

            if (hasPermission(store.state.user, "COACHS_PICTURES_READ")) {
              return { name: "coachs-pictures" };
            }

            return;
          },
          children: [
            {
              path: "users",
              name: "users",
              meta: {
                label: "Prestataires",
                requiredPermission: "USERS_READ",
              },
              component: Users,
            },
            {
              name: "documents",
              path: "documents",
              meta: {
                label: "Documents",
                requiredPermission: "DOCUMENTS_READ",
              },
              component: Documents,
            },
            {
              name: "disbursements",
              path: "disbursements",
              meta: {
                label: "Facturation Coach",
                requiredPermission: "DISBURSEMENTS_READ",
              },
              component: Disbursements,
              redirect: () => {
                return { name: "pending-disbursements" };
              },
              children: [
                {
                  name: "pending-disbursements",
                  path: "pending-disbursements",
                  meta: {
                    label: "En attente",
                    requiredPermission: "DISBURSEMENTS_READ",
                  },
                  component: DisbursementsPending,
                },
                {
                  name: "processed-disbursements",
                  path: "processed-disbursements",
                  meta: {
                    label: "Traités",
                    requiredPermission: "DISBURSEMENTS_READ",
                  },
                  component: DisbursementsProcessed,
                },
                {
                  name: "payed-disbursements",
                  path: "payed-disbursements",
                  meta: {
                    label: "Payés",
                    requiredPermission: "DISBURSEMENTS_READ",
                  },
                  component: DisbursementsPayed,
                },
                {
                  name: "blocked-disbursements",
                  path: "blocked-disbursements",
                  meta: {
                    label: "Bloqués",
                    requiredPermission: "DISBURSEMENTS_READ",
                  },
                  component: DisbursementsBlocked,
                },
                {
                  name: "disbursements-troubles",
                  path: "disbursements-troubles",
                  meta: {
                    label: "Contestations",
                    requiredPermission: "DISBURSEMENTS_READ",
                  },
                  component: DisbursementsTroubles,
                },
              ],
            },
            {
              name: "substitutions",
              path: "substitutions",
              meta: {
                label: "Remplacements",
                requiredPermission: "SUBSTITUTIONS_READ",
              },
              component: Substitutions,
            },
            {
              path: "coachs-pictures",
              name: "coachs-pictures",
              meta: {
                label: "Photos",
                requiredPermission: "COACHS_PICTURES_READ",
              },
              component: UsersProfilePictures,
            },
          ],
        },
        {
          path: "b2b",
          name: "b2b",
          meta: {
            label: "B2B",
            requiredPermission: "COMPANIES_MENU_READ",
          },
          component: CompaniesNav,
          redirect: () => {
            if (hasPermission(store.state.user, "COMPANIES_COMPANY_READ")) {
              return { name: "companies-list" };
            }

            if (hasPermission(store.state.user, "PUBLIC_PROCUREMENTS_READ")) {
              return { name: "public-procurements-list" };
            }

            return;
          },
          children: [
            {
              path: "companies",
              name: "companies-list",
              meta: {
                label: "Entreprises",
                requiredPermission: "COMPANIES_COMPANY_READ",
              },
              component: CompaniesList,
              children: [
                {
                  path: ":id",
                  name: "company-details",
                  meta: {
                    label: "Détails de l'entreprise",
                    requiredPermission: "COMPANIES_COMPANY_READ",
                  },
                  component: CompanyDetails,
                },
              ],
            },
            {
              path: "public-procurements",
              name: "public-procurements-list",
              meta: {
                label: "Marchés publics",
                requiredPermission: "PUBLIC_PROCUREMENTS_READ",
              },
              component: PublicProcurementsList,
              children: [
                {
                  path: ":id",
                  name: "public-procurement-details",
                  meta: {
                    label: "Détails du marché public",
                    requiredPermission: "PUBLIC_PROCUREMENTS_READ",
                  },
                  component: CompanyDetails,
                },
              ],
            },
            {
              path: "signature-sheets",
              name: "signature-sheets",
              meta: {
                label: "Émargement",
                requiredPermission: "PUBLIC_PROCUREMENTS_READ",
              },
              component: SignatureSheetsNav,
              redirect: () => {
                return { name: "signature-sheets-list" };
              },
              children: [
                {
                  path: "sheets",
                  name: "signature-sheets-list",
                  meta: {
                    label: "Feuilles d'émargement",
                    requiredPermission: "PUBLIC_PROCUREMENTS_READ",
                  },
                  component: SignatureSheetsList,
                },
                {
                  path: "templates",
                  name: "signature-sheets-templates",
                  meta: {
                    label: "Modèles",
                    requiredPermission: "PUBLIC_PROCUREMENTS_READ",
                  },
                  component: SignatureSheetTemplates,
                },
                {
                  path: "sessions-batches",
                  name: "sessions-batches",
                  meta: {
                    label: "Lots",
                    requiredPermission: "PUBLIC_PROCUREMENTS_READ",
                  },
                  component: SessionsBatches,
                },
              ],
            },
          ],
        },
        {
          path: "users/:id",
          component: UsersDetails,
          name: "user-details",
          meta: {
            label: "Fiche du prestataire",
            requiredPermission: "USERS_READ",
          },
        },
        {
          name: "fidelisation",
          path: "/fidelisation",
          meta: {
            label: "Fidélisation",
            requiredPermission: "FIDELISATION_READ",
          },
          component: Fidelisation,
        },
        {
          name: "conversion",
          path: "/conversion",
          meta: {
            label: "Conversion",
            requiredPermission: "CONVERSION_READ",
          },
          component: Conversion,
        },
        {
          name: "session-schedule-tracking",
          path: "/session-schedule-tracking",
          meta: {
            label: "Suivi de planification",
            requiredPermission: "SESSION_SCHEDULE_TRACKING_READ",
          },
          component: SessionScheduleTracking,
        },
        {
          name: "b2b-tracking",
          path: "/b2b-tracking",
          meta: {
            label: "Suivi B2B",
            requiredPermission: "B2B_TRACKING_READ",
          },
          component: B2BTracking,
        },
        {
          name: "coach-manager",
          path: "/coach-manager",
          meta: {
            label: "Coach manager",
            requiredPermission: "COACH_MANAGER_READ",
          },
          component: CoachManager,
          redirect: () => {
            if (
              hasPermission(
                store.state.user,
                "COACH_MANAGER_ATTRIBUTIONS_TREATED_READ",
              )
            ) {
              return { name: "coach-manager-attributions-treated" };
            }
            if (hasPermission(store.state.user, "COACH_MANAGER_LOST_READ")) {
              return { name: "coach-manager-lost" };
            }
            if (hasPermission(store.state.user, "COACH_MANAGER_STATUS_READ")) {
              return { name: "coach-manager-status" };
            }
            if (
              hasPermission(store.state.user, "COACH_MANAGER_CONVERSION_READ")
            ) {
              return { name: "coach-manager-conversion" };
            }
            if (
              hasPermission(store.state.user, "COACH_MANAGER_LONGEVITY_READ")
            ) {
              return { name: "coach-manager-longevity" };
            }
            if (
              hasPermission(store.state.user, "COACH_MANAGER_SCRAPPING_READ")
            ) {
              return { name: "coach-manager-scrapping" };
            }
            if (
              hasPermission(
                store.state.user,
                "COACH_MANAGER_INTERNAL_NOTES_READ",
              )
            ) {
              return { name: "coach-manager-internal-note" };
            }

            if (
              hasPermission(
                store.state.user,
                "COACH_MANAGER_CLIENTS_NOTES_READ",
              )
            ) {
              return { name: "coach-manager-clients-notes" };
            }

            return;
          },
          children: [
            {
              path: "attributions-treated",
              component: CoachManagerAttributionsTreated,
              name: "coach-manager-attributions-treated",
              meta: {
                label: "Attributions traitées",
                requiredPermission: "COACH_MANAGER_ATTRIBUTIONS_TREATED_READ",
              },
            },
            {
              path: "lost",
              component: CoachManagerLost,
              name: "coach-manager-lost",
              meta: {
                label: "Perte",
                requiredPermission: "COACH_MANAGER_LOST_READ",
              },
            },
            {
              path: "status",
              component: CoachManagerStatus,
              name: "coach-manager-status",
              meta: {
                label: "Status",
                requiredPermission: "COACH_MANAGER_STATUS_READ",
              },
            },
            {
              path: "conversion",
              component: CoachManagerConversion,
              name: "coach-manager-conversion",
              meta: {
                label: "Conversion",
                requiredPermission: "COACH_MANAGER_CONVERSION_READ",
              },
            },
            {
              path: "longevity",
              component: CoachManagerLongevity,
              name: "coach-manager-longevity",
              meta: {
                label: "Longévité",
                requiredPermission: "COACH_MANAGER_LONGEVITY_READ",
              },
            },
            {
              path: "fidelisation",
              component: CoachManagerFidelisation,
              name: "coach-manager-fidelisation",
              meta: {
                label: "Fidelisation",
                requiredPermission: "COACH_MANAGER_FIDELISATION_READ",
              },
            },
            {
              path: "scrapping",
              component: CoachManagerScrapping,
              name: "coach-manager-scrapping",
              meta: {
                label: "Scrapping",
                requiredPermission: "COACH_MANAGER_SCRAPPING_READ",
              },
            },
            {
              path: "internal-note",
              component: CoachManagerInternalNote,
              name: "coach-manager-internal-note",
              meta: {
                label: "Notes interne",
                requiredPermission: "COACH_MANAGER_INTERNAL_NOTES_READ",
              },
            },
            {
              path: "clients-notes",
              component: CoachManagerClientsNote,
              name: "coach-manager-clients-notes",
              meta: {
                label: "Notes clients",
                requiredPermission: "COACH_MANAGER_CLIENTS_NOTES_READ",
              },
            },
          ],
        },
        {
          name: "settings",
          path: "/settings",
          meta: {
            label: "Paramètres",
            requiredPermission: "SETTINGS_READ",
          },
          component: Settings,
          children: [
            {
              path: "concepts",
              name: "concepts",
              meta: {
                label: "Concepts",
                requiredPermission: "SETTINGS_CONCEPT_READ",
              },
              component: SettingsConcepts,
              children: [
                {
                  path: ":id",
                  component: SettingsConceptsDetails,
                  name: "concept-details",
                  meta: {
                    label: "Modification",
                    requiredPermission: "SETTINGS_CONCEPT_READ",
                  },
                },
              ],
            },
            {
              path: "teammembers",
              component: SettingsTeamMembers,
              name: "teammembers",
              meta: {
                label: "Membres de l'équipe",
                requiredPermission: "SETTINGS_TEAM_READ",
              },
              children: [
                {
                  path: ":id",
                  component: SettingsTeamMembersDetails,
                  name: "teammembers-details",
                  meta: {
                    label: "Fiche du membre",
                    requiredPermission: "SETTINGS_TEAM_READ",
                  },
                },
              ],
            },
            {
              path: "configs",
              component: SettingsConfigs,
              name: "configs",
              meta: {
                label: "Configurations",
                requiredPermission: "SETTINGS_CONFIG_READ",
              },
            },
            {
              path: "payment-methods",
              name: "payment-methods",
              meta: {
                label: "Moyens de paiement",
                requiredPermission: "SETTINGS_PAYMENTS_TYPE_READ",
              },
              component: SettingsPaymentMethods,
              children: [
                {
                  path: ":id",
                  component: SettingsPaymentMethodsDetails,
                  name: "payment-method-details",
                  meta: {
                    label: "Modification Moyen de paiement",
                    requiredPermission: "SETTINGS_PAYMENTS_TYPE_READ",
                  },
                },
              ],
            },
            {
              path: "intervenants",
              component: SettingsIntervenants,
              name: "intervenants",
              meta: {
                label: "Prestataires",
                requiredPermission: "SETTINGS_PRESTA_READ",
              },
            },
            {
              path: "merchants",
              name: "merchants",
              meta: {
                label: "Sociétés",
                requiredPermission: "MERCHANTS_READ",
              },
              component: Merchants,
              children: [
                {
                  path: ":id",
                  component: MerchantsDetails,
                  name: "merchant-details",
                  meta: {
                    label: "Modification",
                    requiredPermission: "MERCHANTS_READ",
                  },
                },
              ],
            },
            {
              path: "sap",
              name: "sap",
              meta: { label: "SAP" },
              component: SettingsSap,
            },
          ],
        },
        {
          name: "export",
          path: "/export",
          meta: {
            label: "Export",
            requiredPermission: "EXPORT_READ",
          },
          component: Export,
          redirect: () => {
            if (hasPermission(store.state.user, "EXPORT_CLIENT_READ")) {
              return { name: "export-clients" };
            }

            if (hasPermission(store.state.user, "EXPORT_PROSPECT_READ")) {
              return { name: "export-prospects" };
            }

            if (hasPermission(store.state.user, "EXPORT_ORDER_READ")) {
              return { name: "export-orders" };
            }

            if (hasPermission(store.state.user, "EXPORT_COACH_READ")) {
              return { name: "export-prestataires" };
            }

            if (hasPermission(store.state.user, "EXPORT_MARKETING_FORM_READ")) {
              return { name: "marketing-form" };
            }

            if (hasPermission(store.state.user, "EXPORT_NOVA_READ")) {
              return { name: "export-nova" };
            }

            if (
              hasPermission(store.state.user, "EXPORT_BALANCE_COMPTABLE_READ")
            ) {
              return { name: "accounting-margin-rate" };
            }

            if (hasPermission(store.state.user, "EXPORT_COMPTA_READ")) {
              return { name: "export-compta" };
            }

            return;
          },
          children: [
            {
              path: "clients",
              component: ExportClients,
              name: "export-clients",
              meta: {
                label: "Clients",
                requiredPermission: "EXPORT_CLIENT_READ",
              },
            },
            {
              path: "prospects",
              component: ExportProspects,
              name: "export-prospects",
              meta: {
                label: "Prospects",
                requiredPermission: "EXPORT_PROSPECT_READ",
              },
            },
            {
              path: "orders",
              component: ExportOrders,
              name: "export-orders",
              meta: {
                label: "Commandes",
                requiredPermission: "EXPORT_ORDER_READ",
              },
            },
            {
              path: "prestataires",
              component: ExportPrestataires,
              name: "export-prestataires",
              meta: {
                label: "Prestataires",
                requiredPermission: "EXPORT_COACH_READ",
              },
            },
            {
              path: "marketing-form",
              component: ExportMarketingForm,
              name: "marketing-form",
              meta: {
                label: "Leads X Marketing",
                requiredPermission: "EXPORT_MARKETING_FORM_READ",
              },
            },
            {
              path: "nova",
              component: ExportNova,
              name: "export-nova",
              meta: {
                label: "NOVA",
                requiredPermission: "EXPORT_NOVA_READ",
              },
            },
            {
              path: "accounting-margin-rate",
              component: ExportAccountingMarginRate,
              name: "accounting-margin-rate",
              meta: {
                label: "Suivi marge comptable",
                requiredPermission: "EXPORT_BALANCE_COMPTABLE_READ",
              },
            },
            {
              path: "solde-comptable",
              component: ExportSoldeComptable,
              name: "export-solde-comptable",
              meta: {
                label: "Balance comptable",
                requiredPermission: "EXPORT_BALANCE_COMPTABLE_READ",
              },
            },
            {
              path: "compta",
              component: ExportCompta,
              name: "export-compta",
              meta: {
                label: "Export factures et paiements",
                requiredPermission: "EXPORT_COMPTA_READ",
              },
            },
          ],
        },
        {
          name: "import",
          path: "/import",
          meta: {
            label: "Import",
            requiredPermission: "IMPORT_WRITE",
          },
          component: Import,
          redirect: () => {
            return { name: "import-prestataires" };
          },
          children: [
            {
              path: "prestataires",
              component: ImportPrestataires,
              name: "import-prestataires",
              meta: {
                label: "Prestataires",
                requiredPermission: "IMPORT_WRITE",
              },
            },
          ],
        },
        {
          name: "contents",
          path: "/contents",
          meta: {
            label: "Contenus clients & coachs",
            requiredPermission: "CONTENTS_READ",
          },
          component: Contents,
          children: [
            {
              path: "dynamic-content",
              name: "dynamic-content",
              meta: {
                label: "Contenu pour les applications",
                requiredPermission: "DYNAMIC_CONTENT_READ",
              },
              component: DynamicContentNav,
              redirect: () => {
                if (
                  hasPermission(
                    store.state.user,
                    "DYNAMIC_CONTENT_COACH_APP_READ",
                  )
                ) {
                  return { name: "dynamic-content-coach-app" };
                }

                if (
                  hasPermission(
                    store.state.user,
                    "DYNAMIC_CONTENT_CLIENT_APP_READ",
                  )
                ) {
                  return { name: "dynamic-content-client-app" };
                }

                return;
              },
              children: [
                {
                  path: "coach-app",
                  name: "dynamic-content-coach-app",
                  meta: {
                    label: "Application coach",
                    requiredPermission: "DYNAMIC_CONTENT_COACH_APP_READ",
                  },
                  component: DynamicContentCoachApp,
                  redirect: () => {
                    if (
                      hasPermission(
                        store.state.user,
                        "DYNAMIC_CONTENT_COACH_APP_SPECIAL_EVENTS_READ",
                      )
                    ) {
                      return {
                        name: "dynamic-content-coach-app-special-events",
                      };
                    }

                    if (
                      hasPermission(
                        store.state.user,
                        "DYNAMIC_CONTENT_COACH_APP_APP_UPDATES_READ",
                      )
                    ) {
                      return { name: "dynamic-content-coach-app-app-updates" };
                    }

                    if (
                      hasPermission(
                        store.state.user,
                        "DYNAMIC_CONTENT_COACH_APP_TUTORIALS_READ",
                      )
                    ) {
                      return { name: "dynamic-content-coach-app-tutorials" };
                    }

                    return;
                  },
                  children: [
                    {
                      path: "special-events",
                      name: "dynamic-content-coach-app-special-events",
                      meta: {
                        label: "Événements",
                        requiredPermission:
                          "DYNAMIC_CONTENT_COACH_APP_SPECIAL_EVENTS_READ",
                      },
                      component: DynamicContentCoachAppSpecialEvents,
                    },
                    {
                      path: "app-updates",
                      name: "dynamic-content-coach-app-app-updates",
                      meta: {
                        label: "Notes de mise à jour",
                        requiredPermission:
                          "DYNAMIC_CONTENT_COACH_APP_APP_UPDATES_READ",
                      },
                      component: DynamicContentCoachAppAppUpdates,
                    },
                    {
                      path: "tutorials",
                      name: "dynamic-content-coach-app-tutorials",
                      meta: {
                        label: "Tutoriels",
                        requiredPermission:
                          "DYNAMIC_CONTENT_COACH_APP_TUTORIALS_READ",
                      },
                      component: DynamicContentCoachAppTutorials,
                    },
                  ],
                },
                {
                  path: "client-app",
                  name: "dynamic-content-client-app",
                  meta: {
                    label: "Application client",
                    requiredPermission: "DYNAMIC_CONTENT_CLIENT_APP_READ",
                  },
                  component: DynamicContentClientApp,
                  redirect: () => {
                    if (
                      hasPermission(
                        store.state.user,
                        "DYNAMIC_CONTENT_CLIENT_APP_NEWS_READ",
                      )
                    ) {
                      return { name: "dynamic-content-client-app-news" };
                    }

                    if (
                      hasPermission(
                        store.state.user,
                        "DYNAMIC_CONTENT_CLIENT_APP_NOTIFICATIONS_READ",
                      )
                    ) {
                      return {
                        name: "dynamic-content-client-app-notifications",
                      };
                    }

                    return;
                  },
                  children: [
                    {
                      path: "news",
                      name: "dynamic-content-client-app-news",
                      meta: {
                        label: "Actualités espace client",
                        requiredPermission:
                          "DYNAMIC_CONTENT_CLIENT_APP_NEWS_READ",
                      },
                      component: DynamicContentClientAppNews,
                    },
                    {
                      path: "notifications",
                      name: "dynamic-content-client-app-notifications",
                      meta: {
                        label: "Notifications espace client",
                        requiredPermission:
                          "DYNAMIC_CONTENT_CLIENT_APP_NOTIFICATIONS_READ",
                      },
                      component: DynamicContentClientAppNotifications,
                    },
                  ],
                },
              ],
            },
            {
              path: "faq",
              name: "faq",
              meta: {
                label: "Questions/Réponses",
                requiredPermission: "FAQ_READ",
              },
              component: Faq,
              children: [
                {
                  path: ":id",
                  component: FaqDetails,
                  name: "faq-details",
                  meta: {
                    label: "Modification",
                    requiredPermission: "FAQ_READ",
                  },
                },
              ],
            },
          ],
        },
        {
          name: "stats",
          path: "/stats",
          meta: {
            label: "Statistiques",
            requiredPermission: "STATS_READ",
          },
          component: Stats,
          redirect: () => {
            if (hasPermission(store.state.user, "STATS_MARKETING_READ")) {
              return { name: "marketing" };
            }

            if (hasPermission(store.state.user, "STATS_LOST_READ")) {
              return { name: "lost" };
            }

            if (hasPermission(store.state.user, "STATS_COMMERCIALS_READ")) {
              return { name: "commercials" };
            }

            if (hasPermission(store.state.user, "STATS_SPONSORSHIP_READ")) {
              return { name: "sponsorships" };
            }

            if (hasPermission(store.state.user, "STATS_CA_RATIOS_READ")) {
              return { name: "stats-ca-ratios" };
            }

            return;
          },
          children: [
            {
              path: "marketing",
              name: "marketing",
              meta: {
                label: "Marketing",
                requiredPermission: "STATS_MARKETING_READ",
              },
              component: StatsMarketing,
              redirect: () => {
                if (hasPermission(store.state.user, "STATS_EXPENSE_READ")) {
                  return { name: "expense" };
                }

                if (
                  hasPermission(store.state.user, "STATS_MARKETING_ROAS_READ")
                ) {
                  return { name: "ca-roas" };
                }

                if (
                  hasPermission(
                    store.state.user,
                    "STATS_MARKETING_FORM_EXPENSE_READ",
                  )
                ) {
                  return { name: "form-expense" };
                }

                if (
                  hasPermission(store.state.user, "STATS_MARKETING_KPI_READ")
                ) {
                  return { name: "marketing-kpi-export" };
                }

                return;
              },
              children: [
                {
                  path: "expense",
                  name: "expense",
                  meta: {
                    label: "Dépenses",
                    requiredPermission: "STATS_EXPENSE_READ",
                  },
                  component: StatsExpense,
                },
                {
                  path: "ca-roas",
                  name: "ca-roas",
                  meta: {
                    label: "CA/ROAS",
                    requiredPermission: "STATS_MARKETING_ROAS_READ",
                  },
                  component: StatsROAS,
                },
                {
                  path: "form-expense",
                  name: "form-expense",
                  meta: {
                    label: "Coûts formulaires",
                    requiredPermission: "STATS_MARKETING_FORM_EXPENSE_READ",
                  },
                  component: StatsFormExpense,
                },
                {
                  path: "kpi",
                  name: "marketing-kpi",
                  meta: {
                    label: "KPI",
                    requiredPermission: "STATS_MARKETING_KPI_READ",
                  },
                  component: StatsMarketingKpi,
                },
                {
                  path: "kpi-export",
                  name: "marketing-kpi-export",
                  meta: {
                    label: "KPI Export",
                    requiredPermission: "STATS_MARKETING_KPI_READ",
                  },
                  component: StatsMarketingKpiExport,
                },
              ],
            },
            {
              path: "lost",
              name: "lost",
              meta: {
                label: "Pertes",
                requiredPermission: "STATS_LOST_READ",
              },
              component: StatsLost,
              redirect: () => {
                if (
                  hasPermission(
                    store.state.user,
                    "STATS_LOST_PROSPECTS_CLIENTS_READ",
                  )
                ) {
                  return { name: "lost-prospects-clients" };
                }

                if (
                  hasPermission(store.state.user, "STATS_LOST_SOURCES_READ")
                ) {
                  return { name: "lost-sources" };
                }

                if (hasPermission(store.state.user, "STATS_LOST_MAP_READ")) {
                  return { name: "lost-map" };
                }

                return;
              },
              children: [
                {
                  path: "prospects-clients",
                  name: "lost-prospects-clients",
                  meta: {
                    label: "Prospects - Clients",
                    requiredPermission: "STATS_LOST_PROSPECTS_CLIENTS_READ",
                  },
                  component: StatsLostProspectsClients,
                },
                {
                  path: "sources",
                  name: "lost-sources",
                  meta: {
                    label: "Sources",
                    requiredPermission: "STATS_LOST_SOURCES_READ",
                  },
                  component: StatsLostSources,
                },
                {
                  path: "map",
                  name: "lost-map",
                  meta: {
                    label: "Carte",
                    requiredPermission: "STATS_LOST_MAP_READ",
                  },
                  component: StatsLostMap,
                },
              ],
            },
            {
              path: "commercials",
              name: "commercials",
              meta: {
                label: "Commerciaux",
                requiredPermission: "STATS_COMMERCIALS_READ",
              },
              component: StatsCommercials,
              redirect: () => {
                if (
                  hasPermission(
                    store.state.user,
                    "STATS_STATS_COMMERCIALS_READ",
                  )
                ) {
                  return { name: "statistique-commercials" };
                }

                if (hasPermission(store.state.user, "STATS_ACQUISITION_READ")) {
                  return { name: "acquisition" };
                }

                if (hasPermission(store.state.user, "STATS_PAYMENTS_READ")) {
                  return { name: "cashed-payments" };
                }

                if (hasPermission(store.state.user, "STATS_ATTRIBUTION_READ")) {
                  return { name: "attributions-stats" };
                }

                if (hasPermission(store.state.user, "STATS_MARGIN_RATE_READ")) {
                  return { name: "margin-rate" };
                }

                if (hasPermission(store.state.user, "STATS_B2C2B_READ")) {
                  return { name: "b2c2b" };
                }

                return;
              },
              children: [
                {
                  path: "statistique-commercials",
                  name: "statistique-commercials",
                  meta: {
                    label: "Statistique commerciaux",
                    requiredPermission: "STATS_STATS_COMMERCIALS_READ",
                  },
                  component: StatsStatsCommercialsV2,
                },
                {
                  path: "acquisition",
                  name: "acquisition",
                  meta: {
                    label: "Acquisition/Fidélisation",
                    requiredPermission: "STATS_ACQUISITION_READ",
                  },
                  component: StatsAcquisitionFidelisation,
                },
                {
                  path: "cashed-payments",
                  name: "cashed-payments",
                  meta: {
                    label: "Paiements encaissés",
                    requiredPermission: "STATS_PAYMENTS_READ",
                  },
                  component: StatsPayments,
                },
                {
                  path: "attributions-stats",
                  name: "attributions-stats",
                  meta: {
                    label: "Attribution",
                    requiredPermission: "STATS_ATTRIBUTION_READ",
                  },
                  component: StatsAttribution,
                },
                {
                  path: "margin-rate",
                  name: "margin-rate",
                  meta: {
                    label: "Taux de marge",
                    requiredPermission: "STATS_MARGIN_RATE_READ",
                  },
                  component: StatsMarginRate,
                },
                {
                  path: "b2c2b",
                  name: "b2c2b",
                  meta: {
                    label: "B2C2B",
                    requiredPermission: "STATS_B2C2B_READ",
                  },
                  component: StatsB2C2B,
                },
              ],
            },
            {
              path: "ca-ratios",
              name: "stats-ca-ratios",
              meta: {
                label: "CA Ratios",
                requiredPermission: "STATS_CA_RATIOS_READ",
              },
              component: StatsCARatios,
              children: [
                {
                  path: "couts",
                  name: "statistique-ca-ratios-couts",
                  meta: {
                    label: "Coûts",
                    requiredPermission: "STATS_CA_RATIOS_READ",
                  },
                  component: StatsCARatiosCouts,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  try {
    window.gtag("event", "page_view", {
      url: to.path,
    });

    if (to.path === "/") {
      if (hasPermission(store.state.user, "DASHBOARD_READ"))
        return next({ name: "dashboard" });
      if (hasPermission(store.state.user, "MYACCOUNT_READ"))
        return next({ name: "account" });
    }

    if (to.meta.requiredPermission) {
      if (!store.state.user) {
        try {
          const { data } = await Vue.api.post("/users/refresh-token");
          store.commit("AUTH_TOKEN", data.accessToken);
        } catch (_) {
          // refresh failed, will redirect to login below
        }
      }

      let isAllowed = false;

      if (Array.isArray(to.meta.requiredPermission)) {
        for (let permission of to.meta.requiredPermission) {
          if (hasPermission(store.state.user, permission)) {
            isAllowed = true;
          }
        }
      } else if (hasPermission(store.state.user, to.meta.requiredPermission)) {
        isAllowed = true;
      }

      if (!isAllowed) {
        if (hasPermission(store.state.user, "DASHBOARD_READ"))
          return next({ name: "dashboard" });
        if (hasPermission(store.state.user, "MYACCOUNT_READ"))
          return next({ name: "account" });
        return store.state.user ? next(false) : next({ name: "login" });
      }
    }

    if (to && to.matched) {
      const nearestWithTitle = to.matched
        .slice()
        .reverse()
        .find((r) => r.meta && r.meta.label);

      if (nearestWithTitle) {
        const parents = [];

        let routeCheckedForParents = nearestWithTitle;

        while (
          routeCheckedForParents.parent &&
          routeCheckedForParents.parent.name !== "Home"
        ) {
          const { parent } = routeCheckedForParents;

          parents.push(parent);

          routeCheckedForParents = parent;
        }

        let parentsString = "";

        if (parents.length > 0) {
          for (const parent of parents.reverse()) {
            if (parent.meta.label) {
              parentsString = parentsString + `${parent.meta.label} / `;
            }
          }
        }

        document.title =
          "moov'on - " + parentsString + nearestWithTitle.meta.label;
      }
    }
  } catch (e) {
    return next({ name: "login" });
  }

  next();
});

export default router;
